<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useStatistics } from "@/composables/useStatistics";
import type { Statistics } from "@/services/statisticsService";
import { logError } from "@/utils/secureLogger";

// Reactive numbers for counting animation
const usersCount = ref(0);
const ratingCount = ref(0);

// Target values - will be loaded from database
const usersTarget = ref(1401);
const ratingTarget = ref(4.9);

const { statistics, loadStatistics } = useStatistics();

const syncFooterStatistics = (stats: Statistics | null) => {
  if (!stats) {
    return;
  }

  usersTarget.value = stats.totalUsers;
  ratingTarget.value = stats.averageRating;
};

watch(
  statistics,
  (stats) => {
    syncFooterStatistics(stats);
  },
  { immediate: true }
);

// Animation state
const isAnimating = ref(false);
const footerRef = ref<HTMLElement>();

// Intersection Observer for triggering animation when footer is visible
let observer: IntersectionObserver | null = null;

// Counting animation function
const animateCount = (current: { value: number }, target: number, duration: number = 2000) => {
  const start = performance.now();
  const startValue = current.value;

  const animate = (currentTime: number) => {
    const elapsed = currentTime - start;
    const progress = Math.min(elapsed / duration, 1);

    // Easing function (ease-out)
    const easedProgress = 1 - Math.pow(1 - progress, 3);

    current.value = Math.floor(startValue + (target - startValue) * easedProgress);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      current.value = target;
    }
  };

  requestAnimationFrame(animate);
};

// Decimal counting animation function for ratings
const animateDecimal = (current: { value: number }, target: number, duration: number = 2000) => {
  const start = performance.now();
  const startValue = current.value;

  const animate = (currentTime: number) => {
    const elapsed = currentTime - start;
    const progress = Math.min(elapsed / duration, 1);

    // Easing function (ease-out)
    const easedProgress = 1 - Math.pow(1 - progress, 3);

    current.value = Math.round((startValue + (target - startValue) * easedProgress) * 10) / 10;

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      current.value = target;
    }
  };

  requestAnimationFrame(animate);
};

// Start counting animation
const startCounting = () => {
  if (isAnimating.value) return;

  isAnimating.value = true;

  // Reset counts
  usersCount.value = 0;
  ratingCount.value = 0;

  // Start animations with slight delays for staggered effect
  setTimeout(() => animateCount(usersCount, usersTarget.value, 2000), 0);
  setTimeout(() => animateDecimal(ratingCount, ratingTarget.value, 2000), 200);
};

// Setup intersection observer
onMounted(async () => {
  try {
    await loadStatistics();
  } catch (error) {
    logError("Footer: unable to load statistics", error);
  }

  if (footerRef.value) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isAnimating.value) {
            startCounting();
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of footer is visible
        rootMargin: "0px 0px -100px 0px", // Start animation a bit before footer is fully visible
      }
    );

    observer.observe(footerRef.value);
  }
});

// Cleanup
onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});
</script>

<template>
  <div
    class="property1default w-full relative min-h-[400px] md:min-h-[300px] h-auto text-left text-[20px] text-white font-['Inter'] py-[40px] md:py-[60px] overflow-hidden"
    ref="footerRef"
  >
    <div class="property1defaultChild absolute inset-0 bg-[#111827] z-[1]" />

    <!-- Footer Container with 3 Columns -->
    <div
      class="footer-container relative z-[2] flex flex-col md:flex-row justify-between items-stretch gap-[40px] px-[20px] md:px-[80px] max-w-[1400px] mx-auto min-h-[250px] text-center md:text-left"
    >
      <!-- Column 1: Logo and Description -->
      <div
        class="footer-column flex flex-col gap-[30px] items-center md:items-start flex-1 min-w-[200px]"
      >
        <picture>
          <source srcset="/full-icon-white.webp" type="image/webp" />
          <img
            class="footer-logo w-full h-[55px] md:h-[3em] object-contain object-left"
            alt="ElevatED Logo"
            src="/full-icon-white.png"
            width="200"
            height="200"
          />
        </picture>
        <div
          class="empoweringSeniorHigh text-white font-semibold text-[16px] md:text-[18px] leading-[24px] md:leading-[28px] text-center md:text-left max-w-full md:max-w-[280px]"
        >
          Empowering Senior High School students through tutoring, Al-powered learning tools, and
          personalized academic support.
        </div>
      </div>

      <!-- Column Divider -->
      <div class="column-divider hidden md:block w-px h-full bg-white/20 self-stretch"></div>

      <!-- Column 2: Statistics -->
      <div
        class="footer-column footer-column-center flex flex-col items-center justify-center text-center flex-1 min-w-[250px]"
      >
        <div class="stats-container w-full max-w-[550px]">
          <div class="stats-grid flex flex-row gap-0 items-stretch justify-center">
            <div class="stat-item flex-1 py-4">
              <div
                class="stat-value text-[42px] md:text-[56px] font-extrabold leading-none text-[#93dfff] mb-3"
              >
                {{ usersCount }}
              </div>
              <div
                class="stat-label text-white/70 text-[14px] md:text-[15px] font-semibold uppercase tracking-wider"
              >
                Total Users
              </div>
            </div>
            <div
              class="stat-divider w-px bg-gradient-to-b from-transparent via-white/20 to-transparent mx-8 md:mx-12"
            ></div>
            <div class="stat-item flex-1 py-4">
              <div
                class="stat-value text-[42px] md:text-[56px] font-extrabold leading-none text-[#fbff8d] mb-3"
              >
                {{ ratingCount.toFixed(1)
                }}<span
                  class="rating-max text-[24px] md:text-[32px] text-[#fbff8d]/40 font-bold ml-1"
                  >/5.0</span
                >
              </div>
              <div
                class="stat-label text-white/70 text-[14px] md:text-[15px] font-semibold uppercase tracking-wider"
              >
                Average Tutor Rating
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Column Divider -->
      <div class="column-divider hidden md:block w-px h-full bg-white/20 self-stretch"></div>

      <!-- Column 3: Navigation -->
      <div
        class="footer-column flex flex-col gap-[30px] items-center md:items-start flex-1 min-w-[200px]"
      >
        <div class="footer-navigation flex flex-col gap-[20px] w-full">
          <div class="nav-item block w-full">
            <router-link to="/about" class="nav-link group">
              <span
                class="aboutUs m-0 text-[18px] md:text-[20px] font-bold text-white/90 group-hover:text-white flex items-center justify-center md:justify-between w-full py-[8px] transition-all duration-200"
                >About Us
                <img
                  class="nav-arrow opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200"
                  alt=""
                  src="/footer-arrow.svg"
                />
              </span>
            </router-link>
          </div>
          <div class="nav-item block w-full">
            <router-link to="/privacy-policy" class="nav-link group">
              <span
                class="aboutUs m-0 text-[18px] md:text-[20px] font-bold text-white/90 group-hover:text-white flex items-center justify-center md:justify-between w-full py-[8px] transition-all duration-200"
                >Privacy Policy
                <img
                  class="nav-arrow opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200"
                  alt=""
                  src="/footer-arrow.svg"
                />
              </span>
            </router-link>
          </div>
          <div class="nav-item block w-full">
            <router-link to="/terms-conditions" class="nav-link group">
              <span
                class="aboutUs m-0 text-[18px] md:text-[20px] font-bold text-white/90 group-hover:text-white flex items-center justify-center md:justify-between w-full py-[8px] transition-all duration-200"
                >Terms & Conditions
                <img
                  class="nav-arrow opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200"
                  alt=""
                  src="/footer-arrow.svg"
                />
              </span>
            </router-link>
          </div>
          <div class="nav-item block w-full">
            <router-link to="/about#faq-section" class="nav-link group">
              <span
                class="aboutUs m-0 text-[18px] md:text-[20px] font-bold text-white/90 group-hover:text-white flex items-center justify-center md:justify-between w-full py-[8px] transition-all duration-200"
                >FAQs
                <img
                  class="nav-arrow opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200"
                  alt=""
                  src="/footer-arrow.svg"
                />
              </span>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Footer Background */
.property1defaultChild {
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0%;
  right: 0%;
  bottom: 0%;
  left: 0%;
  background-color: #111827;
  z-index: 1;
}

/* Main Footer Container */
.property1default {
  width: 100%;
  position: relative;
  min-height: 300px;
  height: auto;
  text-align: left;
  font-size: 20px;
  color: #fff;
  font-family: Inter;
  padding: 60px 0;
  overflow: hidden;
}

/* Footer Grid Container */
.footer-container {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 40px;
  padding: 0 80px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 250px;
}

/* Column Styling */
.footer-column {
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: flex-start;
  flex: 1;
  min-width: 200px;
}

/* Ensure first column stays left-aligned */
.footer-column:first-child {
  align-items: flex-start;
}

@media (max-width: 768px) {
  .footer-column:first-child {
    align-items: center;
  }
}

/* Column Dividers */
.column-divider {
  width: 1px;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.2);
  align-self: stretch;
}

/* Column 1: Logo and Description */
.footer-logo {
  width: 100%;
  height: 3em;
  object-fit: contain;
  object-position: left center;
}

.empoweringSeniorHigh {
  line-height: 28px;
  font-weight: 600;
  font-size: 18px;
  color: #fff;
  max-width: 280px;
}

/* Column 2: Statistics - Centered */
.footer-column-center {
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
}

.stats-container {
  width: 100%;
  max-width: 550px;
}

.stats-grid {
  display: flex;
  flex-direction: row;
  gap: 0;
  align-items: stretch;
  justify-content: center;
}

.stat-item {
  text-align: center;
  flex: 1;
  padding: 1rem 0;
  transition: transform 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-4px);
}

.stat-value {
  font-size: 56px;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 0.75rem;
  transition: all 0.3s ease;
}

.stat-item:hover .stat-value {
  transform: scale(1.05);
}

.stat-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 15px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-divider {
  width: 1px;
  background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.2), transparent);
  margin: 0 3rem;
}

/* Column 3: Navigation */
.footer-navigation {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.nav-item {
  display: block;
  width: 100%;
}

.nav-link {
  display: block;
  text-decoration: none;
  color: inherit;
  width: 100%;
}

.nav-link {
  position: relative;
}

.nav-link::before {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #93dfff, #fbff8d);
  transition: width 0.3s ease;
}

.nav-link:hover::before {
  width: 100%;
}

.aboutUs {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 0;
  transition: all 0.2s ease;
}

.nav-arrow {
  width: 12px;
  height: 16px;
  object-fit: contain;
  margin-left: 10px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .property1default {
    min-height: 400px;
    padding: 40px 0;
  }

  .footer-container {
    flex-direction: column;
    gap: 40px;
    padding: 0 20px;
    text-align: center;
  }

  .column-divider {
    display: none;
  }

  .footer-column {
    align-items: center;
  }

  .footer-column-center {
    align-items: center !important;
  }

  .stats-container {
    max-width: 100%;
  }

  .stats-grid {
    gap: 0;
  }

  .stat-divider {
    margin: 0 2rem;
  }

  .stat-item {
    padding: 0.75rem 0;
  }

  .stat-value {
    font-size: 42px;
  }

  .rating-max {
    font-size: 24px;
  }

  .stat-label {
    font-size: 13px;
  }

  .empoweringSeniorHigh {
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    max-width: 100%;
  }

  .aboutUs {
    font-size: 18px;
    justify-content: center;
  }

  .nav-link {
    display: block;
    text-decoration: none;
    color: inherit;
  }

  .nav-arrow {
    width: 10px;
    height: 12px;
  }

  .footer-logo {
    width: 100%;
    height: 55px;
    object-fit: contain;
    object-position: center center;
  }
}

@media (max-width: 480px) {
  .footer-container {
    gap: 30px;
    padding: 0 15px;
  }

  .stats-grid {
    flex-direction: column;
    gap: 1.5rem;
  }

  .stat-divider {
    display: none;
  }

  .stat-item {
    padding: 0.5rem 0;
  }

  .stat-value {
    font-size: 36px;
  }

  .rating-max {
    font-size: 20px;
  }

  .stat-label {
    font-size: 12px;
  }

  .empoweringSeniorHigh {
    font-size: 14px;
    line-height: 20px;
  }

  .aboutUs {
    font-size: 16px;
  }

  .nav-link {
    display: block;
    text-decoration: none;
    color: inherit;
  }
}
</style>
